import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import {
  Search,
  Filter,
  Users,
  Building2,
  Clock,
  Mail,
  MapPin,
  Calendar,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  AlertCircle,
  X,
  Eye,
} from "lucide-react";
import Loading from "../components/Loading";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://api.littleexplorers.help";

const TYPE_OPTIONS = [
  { value: "all", label: "All Feedback", icon: MessageSquare },
  { value: "parent", label: "Parent Feedback", icon: Users },
  { value: "business", label: "Business Feedback", icon: Building2 },
];

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const Feedback = () => {
  const { getAccessToken } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedType, setSelectedType] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());

  useEffect(() => {
    fetchData();
  }, [selectedType]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const token = getAccessToken();
      if (!token) return;

      // Fetch submissions
      const queryParams = new URLSearchParams();
      if (selectedType !== "all") {
        queryParams.append("type", selectedType);
      }
      queryParams.append("limit", "100");

      const [submissionsRes, statsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/admin/feedback?${queryParams}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_BASE_URL}/admin/feedback/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (!submissionsRes.ok) throw new Error("Failed to fetch feedback");
      if (!statsRes.ok) throw new Error("Failed to fetch stats");

      const submissionsData = await submissionsRes.json();
      const statsData = await statsRes.json();

      setSubmissions(submissionsData.submissions || []);
      setStats(statsData);
    } catch (err) {
      console.error("Error fetching feedback:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredSubmissions = submissions.filter((submission) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      submission.name?.toLowerCase().includes(query) ||
      submission.email?.toLowerCase().includes(query) ||
      submission.parentData?.hardestPart?.toLowerCase().includes(query) ||
      submission.businessData?.organizationName?.toLowerCase().includes(query)
    );
  });

  const toggleRow = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const openDetailModal = (submission) => {
    setSelectedSubmission(submission);
    setShowDetailModal(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[var(--text-primary)] mb-1">
            Feedback Submissions
          </h1>
          <p className="text-[var(--text-tertiary)]">
            View and manage parent and business feedback
          </p>
        </div>
        <button
          onClick={fetchData}
          className="btn-secondary flex items-center gap-2"
          disabled={isLoading}
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card p-4">
            <p className="text-2xl font-bold text-[var(--text-primary)]">
              {stats.totalSubmissions}
            </p>
            <p className="text-sm text-[var(--text-tertiary)]">
              Total Submissions
            </p>
          </div>
          <div className="card p-4">
            <p className="text-2xl font-bold text-[var(--primary-600)]">
              {stats.parentFeedback.total}
            </p>
            <p className="text-sm text-[var(--text-tertiary)]">
              Parent Feedback
            </p>
          </div>
          <div className="card p-4">
            <p className="text-2xl font-bold text-[var(--secondary-600)]">
              {stats.businessFeedback.total}
            </p>
            <p className="text-sm text-[var(--text-tertiary)]">
              Business Feedback
            </p>
          </div>
          <div className="card p-4">
            <p className="text-2xl font-bold text-green-600">
              {stats.parentFeedback.last24h + stats.businessFeedback.last24h}
            </p>
            <p className="text-sm text-[var(--text-tertiary)]">Last 24 Hours</p>
          </div>
        </div>
      )}

      {/* Type Filter */}
      <div className="grid grid-cols-3 gap-4">
        {TYPE_OPTIONS.map((option) => {
          const Icon = option.icon;
          const count =
            option.value === "all"
              ? stats?.totalSubmissions || 0
              : option.value === "parent"
              ? stats?.parentFeedback.total || 0
              : stats?.businessFeedback.total || 0;

          return (
            <button
              key={option.value}
              onClick={() => setSelectedType(option.value)}
              className={`card p-4 text-left transition-all ${
                selectedType === option.value
                  ? "ring-2 ring-[var(--primary-500)]"
                  : ""
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    option.value === "parent"
                      ? "bg-green-100 text-green-600"
                      : option.value === "business"
                      ? "bg-amber-100 text-amber-600"
                      : "bg-blue-100 text-blue-600"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[var(--text-primary)]">
                    {count}
                  </p>
                  <p className="text-sm text-[var(--text-tertiary)]">
                    {option.label}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-muted)]" />
        <input
          type="text"
          placeholder="Search by name, email, or content..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="input pl-10 w-full"
        />
      </div>

      {/* Error Message */}
      {error && (
        <div className="card p-4 border-red-200 bg-red-50 dark:bg-red-900/20">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="w-5 h-5" />
            <p>{error}</p>
          </div>
        </div>
      )}

      {/* Submissions Table */}
      <div className="card overflow-hidden">
        {filteredSubmissions.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-[var(--bg-tertiary)] flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="w-8 h-8 text-[var(--text-muted)]" />
            </div>
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-1">
              No feedback found
            </h3>
            <p className="text-[var(--text-tertiary)]">
              {searchQuery
                ? "Try adjusting your search terms"
                : "No feedback submissions match the selected filter"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[var(--bg-secondary)] border-b border-[var(--border-primary)]">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Type
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Name / Organization
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Key Info
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-primary)]">
                {filteredSubmissions.map((submission) => (
                  <React.Fragment key={submission.id}>
                    <tr
                      className="hover:bg-[var(--bg-secondary)] transition-colors cursor-pointer"
                      onClick={() => toggleRow(submission.id)}
                    >
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                            submission.type === "parent"
                              ? "bg-green-100 text-green-800 border-green-200"
                              : "bg-amber-100 text-amber-800 border-amber-200"
                          }`}
                        >
                          {submission.type === "parent" ? (
                            <Users className="w-3 h-3" />
                          ) : (
                            <Building2 className="w-3 h-3" />
                          )}
                          {submission.type === "parent" ? "Parent" : "Business"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-medium text-[var(--text-primary)]">
                          {submission.name || "Anonymous"}
                        </p>
                        {submission.type === "business" &&
                          submission.businessData?.organizationName && (
                            <p className="text-sm text-[var(--text-muted)]">
                              {submission.businessData.organizationName}
                            </p>
                          )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1 text-[var(--text-secondary)]">
                          <Mail className="w-4 h-4" />
                          <span className="text-sm">{submission.email}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {submission.type === "parent" ? (
                          <div className="text-sm text-[var(--text-secondary)]">
                            {submission.parentData?.kidsAges?.length > 0 && (
                              <span>
                                Kids:{" "}
                                {submission.parentData.kidsAges.join(", ")}
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="text-sm text-[var(--text-secondary)]">
                            {submission.businessData?.activityType && (
                              <span>
                                {submission.businessData.activityType}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-[var(--text-secondary)]">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(submission.submittedAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openDetailModal(submission);
                          }}
                          className="btn-secondary py-1.5 px-3 text-sm"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                    {expandedRows.has(submission.id) && (
                      <tr className="bg-[var(--bg-secondary)]">
                        <td colSpan={6} className="px-6 py-4">
                          <div className="space-y-3">
                            {submission.type === "parent" &&
                              submission.parentData && (
                                <>
                                  {submission.parentData.hardestPart && (
                                    <div>
                                      <span className="text-xs font-medium text-[var(--text-muted)] uppercase">
                                        Hardest Part:
                                      </span>
                                      <p className="text-sm text-[var(--text-primary)] mt-1">
                                        {submission.parentData.hardestPart}
                                      </p>
                                    </div>
                                  )}
                                  {submission.parentData.searchFrequency && (
                                    <div>
                                      <span className="text-xs font-medium text-[var(--text-muted)] uppercase">
                                        Search Frequency:
                                      </span>
                                      <p className="text-sm text-[var(--text-primary)] mt-1">
                                        {submission.parentData.searchFrequency}
                                      </p>
                                    </div>
                                  )}
                                  {submission.parentData.constraints?.length >
                                    0 && (
                                    <div>
                                      <span className="text-xs font-medium text-[var(--text-muted)] uppercase">
                                        Constraints:
                                      </span>
                                      <div className="flex flex-wrap gap-2 mt-1">
                                        {submission.parentData.constraints.map(
                                          (c, i) => (
                                            <span
                                              key={i}
                                              className="px-2 py-1 bg-[var(--bg-primary)] rounded text-xs text-[var(--text-secondary)]"
                                            >
                                              {c}
                                            </span>
                                          ),
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  {submission.parentData.stickyFeatures
                                    ?.length > 0 && (
                                    <div>
                                      <span className="text-xs font-medium text-[var(--text-muted)] uppercase">
                                        Desired Features:
                                      </span>
                                      <div className="flex flex-wrap gap-2 mt-1">
                                        {submission.parentData.stickyFeatures.map(
                                          (f, i) => (
                                            <span
                                              key={i}
                                              className="px-2 py-1 bg-[var(--bg-primary)] rounded text-xs text-[var(--text-secondary)]"
                                            >
                                              {f}
                                            </span>
                                          ),
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  {submission.parentData.planInterest && (
                                    <div>
                                      <span className="text-xs font-medium text-[var(--text-muted)] uppercase">
                                        Plan Interest:
                                      </span>
                                      <p className="text-sm text-[var(--text-primary)] mt-1">
                                        {submission.parentData.planInterest}
                                        {submission.parentData
                                          .willingnessToPay &&
                                          ` (Willing to pay: ${submission.parentData.willingnessToPay})`}
                                      </p>
                                    </div>
                                  )}
                                  {submission.parentData.wouldRecommend && (
                                    <div>
                                      <span className="text-xs font-medium text-[var(--text-muted)] uppercase">
                                        Would Recommend:
                                      </span>
                                      <p className="text-sm text-[var(--text-primary)] mt-1">
                                        {submission.parentData.wouldRecommend}
                                      </p>
                                    </div>
                                  )}
                                </>
                              )}

                            {submission.type === "business" &&
                              submission.businessData && (
                                <>
                                  {submission.businessData
                                    .currentDiscoveryChannels?.length > 0 && (
                                    <div>
                                      <span className="text-xs font-medium text-[var(--text-muted)] uppercase">
                                        Current Discovery Channels:
                                      </span>
                                      <div className="flex flex-wrap gap-2 mt-1">
                                        {submission.businessData.currentDiscoveryChannels.map(
                                          (c, i) => (
                                            <span
                                              key={i}
                                              className="px-2 py-1 bg-[var(--bg-primary)] rounded text-xs text-[var(--text-secondary)]"
                                            >
                                              {c}
                                            </span>
                                          ),
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  {submission.businessData.dataInsightsWanted
                                    ?.length > 0 && (
                                    <div>
                                      <span className="text-xs font-medium text-[var(--text-muted)] uppercase">
                                        Data Insights Wanted:
                                      </span>
                                      <div className="flex flex-wrap gap-2 mt-1">
                                        {submission.businessData.dataInsightsWanted.map(
                                          (i, idx) => (
                                            <span
                                              key={idx}
                                              className="px-2 py-1 bg-[var(--bg-primary)] rounded text-xs text-[var(--text-secondary)]"
                                            >
                                              {i}
                                            </span>
                                          ),
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedSubmission && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-[var(--bg-primary)] rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[var(--border-primary)] flex items-center justify-between">
              <h2 className="text-xl font-bold text-[var(--text-primary)]">
                Feedback Details
              </h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">
                    Type
                  </label>
                  <span
                    className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                      selectedSubmission.type === "parent"
                        ? "bg-green-100 text-green-800 border-green-200"
                        : "bg-amber-100 text-amber-800 border-amber-200"
                    }`}
                  >
                    {selectedSubmission.type === "parent" ? (
                      <Users className="w-3 h-3" />
                    ) : (
                      <Building2 className="w-3 h-3" />
                    )}
                    {selectedSubmission.type === "parent"
                      ? "Parent"
                      : "Business"}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">
                    Submitted
                  </label>
                  <p className="text-[var(--text-primary)]">
                    {formatDate(selectedSubmission.submittedAt)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">
                    Name
                  </label>
                  <p className="text-[var(--text-primary)]">
                    {selectedSubmission.name || "Anonymous"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">
                    Email
                  </label>
                  <p className="text-[var(--text-primary)]">
                    {selectedSubmission.email}
                  </p>
                </div>
              </div>

              {/* Parent Data */}
              {selectedSubmission.type === "parent" &&
                selectedSubmission.parentData && (
                  <div className="space-y-4 border-t border-[var(--border-primary)] pt-4">
                    <h3 className="font-semibold text-[var(--text-primary)]">
                      Parent Feedback
                    </h3>

                    {selectedSubmission.parentData.kidsAges?.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">
                          Kids&apos; Ages
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {selectedSubmission.parentData.kidsAges.map(
                            (age, i) => (
                              <span
                                key={i}
                                className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm"
                              >
                                {age}
                              </span>
                            ),
                          )}
                        </div>
                      </div>
                    )}

                    {selectedSubmission.parentData.hardestPart && (
                      <div>
                        <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">
                          Hardest Part About Finding Activities
                        </label>
                        <p className="text-[var(--text-primary)] bg-[var(--bg-secondary)] p-3 rounded-lg">
                          {selectedSubmission.parentData.hardestPart}
                        </p>
                      </div>
                    )}

                    {selectedSubmission.parentData.searchFrequency && (
                      <div>
                        <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">
                          Search Frequency
                        </label>
                        <p className="text-[var(--text-primary)]">
                          {selectedSubmission.parentData.searchFrequency}
                        </p>
                      </div>
                    )}

                    {selectedSubmission.parentData.constraints?.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">
                          Scheduling Constraints
                        </label>
                        <ul className="list-disc list-inside text-[var(--text-secondary)]">
                          {selectedSubmission.parentData.constraints.map(
                            (c, i) => (
                              <li key={i}>{c}</li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}

                    {selectedSubmission.parentData.stickyFeatures?.length >
                      0 && (
                      <div>
                        <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">
                          Desired Features
                        </label>
                        <ul className="list-disc list-inside text-[var(--text-secondary)]">
                          {selectedSubmission.parentData.stickyFeatures.map(
                            (f, i) => (
                              <li key={i}>{f}</li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}

                    {selectedSubmission.parentData.planInterest && (
                      <div>
                        <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">
                          Plan Interest
                        </label>
                        <p className="text-[var(--text-primary)]">
                          {selectedSubmission.parentData.planInterest}
                        </p>
                      </div>
                    )}

                    {selectedSubmission.parentData.willingnessToPay && (
                      <div>
                        <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">
                          Willingness to Pay
                        </label>
                        <p className="text-[var(--text-primary)]">
                          {selectedSubmission.parentData.willingnessToPay}
                        </p>
                      </div>
                    )}

                    {selectedSubmission.parentData.wouldRecommend && (
                      <div>
                        <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">
                          Would Recommend
                        </label>
                        <p className="text-[var(--text-primary)]">
                          {selectedSubmission.parentData.wouldRecommend}
                        </p>
                      </div>
                    )}

                    {selectedSubmission.parentData.source && (
                      <div>
                        <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">
                          How They Heard About Us
                        </label>
                        <p className="text-[var(--text-primary)]">
                          {selectedSubmission.parentData.source}
                        </p>
                      </div>
                    )}
                  </div>
                )}

              {/* Business Data */}
              {selectedSubmission.type === "business" &&
                selectedSubmission.businessData && (
                  <div className="space-y-4 border-t border-[var(--border-primary)] pt-4">
                    <h3 className="font-semibold text-[var(--text-primary)]">
                      Business Feedback
                    </h3>

                    {selectedSubmission.businessData.organizationName && (
                      <div>
                        <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">
                          Organization
                        </label>
                        <p className="text-[var(--text-primary)] font-medium">
                          {selectedSubmission.businessData.organizationName}
                        </p>
                      </div>
                    )}

                    {selectedSubmission.businessData.activityType && (
                      <div>
                        <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">
                          Activity Type
                        </label>
                        <p className="text-[var(--text-primary)]">
                          {selectedSubmission.businessData.activityType}
                        </p>
                      </div>
                    )}

                    {selectedSubmission.businessData.currentDiscoveryChannels
                      ?.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">
                          Current Discovery Channels
                        </label>
                        <ul className="list-disc list-inside text-[var(--text-secondary)]">
                          {selectedSubmission.businessData.currentDiscoveryChannels.map(
                            (c, i) => (
                              <li key={i}>{c}</li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}

                    {selectedSubmission.businessData.dataInsightsWanted
                      ?.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">
                          Data Insights Wanted
                        </label>
                        <ul className="list-disc list-inside text-[var(--text-secondary)]">
                          {selectedSubmission.businessData.dataInsightsWanted.map(
                            (i, idx) => (
                              <li key={idx}>{i}</li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

              {/* Metadata */}
              <div className="border-t border-[var(--border-primary)] pt-4 text-sm text-[var(--text-muted)]">
                <p>ID: {selectedSubmission.id}</p>
                {selectedSubmission.ipAddress && (
                  <p>IP: {selectedSubmission.ipAddress}</p>
                )}
                {selectedSubmission.userAgent && (
                  <p className="truncate">
                    User-Agent: {selectedSubmission.userAgent}
                  </p>
                )}
                {selectedSubmission.referrer && (
                  <p>Referrer: {selectedSubmission.referrer}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Feedback;
